import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';

const NavigationContext = React.createContext(undefined);

// Actions
const SET_NAVIGATION = 'set-navigation-collection';
const NAVIGATION_LOADING_STARTED = 'navigation-started';
const NAVIGATION_LOADING_SUCCEEDED = 'navigation-succeeded';
const NAVIGATION_LOADING_FAILED = 'navigation-load-failed';

const SET_OPEN = 'set-navigation-open';

const init = () => {
  return {
    isInitializing: true,
    navigationCollection: null,
    navigationResource: null,
    isNavigationLoading: false,
    navigationLoadingError: null,
    open: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case NAVIGATION_LOADING_STARTED: {
      return {
        ...state,
        isNavigationLoading: true,
        navigationLoadingError: null,
      };
    }
    case NAVIGATION_LOADING_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        isNavigationLoading: false,
        navigationLoadingError: null,
      };
    }
    case NAVIGATION_LOADING_FAILED: {
      return {
        ...state,
        isInitializing: false,
        isNavigationLoading: false,
        navigationLoadingError: action.navigationLoadingError,
      };
    }
    case SET_NAVIGATION: {
      return {
        ...state,
        isInitializing: false,
        isNavigationLoading: false,
        navigationCollection: action.navigationCollection,
        navigationResource: action.navigationCollection?.resources()?.head(),
      };
    }
    case SET_OPEN: {
      return {
        ...state,
        open: action.open,
      };
    }
    default: {
      throw new Error(`Unhandled NavigationContext action type: ${action.type}`);
    }
  }
};

const NavigationProvider = ({
  navigationHeaderLeft,
  navigationHeaderRight,
  children,
  pageResource,
  loadPageLink,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  // If you want to allow rendering your navigation within a portal, use navPortal
  const [navPortal, setNavPortal] = useState(null);

  const setNavigationCollection = React.useCallback((navColl) => {
    dispatch({ type: SET_NAVIGATION, navigationCollection: navColl });
  }, []);

  const loadNavigationLink = React.useCallback(async (navLink) => {
    if (!navLink) {
      throw new Error('loadNavigation Error: Link not present');
    }

    dispatch({ type: NAVIGATION_LOADING_STARTED });

    try {
      const updatedColl = await navLink.get();
      setNavigationCollection(updatedColl);
      dispatch({ type: NAVIGATION_LOADING_SUCCEEDED });
    } catch (err) {
      dispatch({ type: NAVIGATION_LOADING_FAILED, navigationLoadingError: err });
    }
  }, []);

  useEffect(() => {
    dispatch({ type: SET_OPEN, open: false });
  }, [state.navigationResource?.type()]);

  const refreshNavigationCollection = React.useCallback(async () => {
    const { navigationCollection: navigationColl } = state;

    if (navigationColl === null) {
      return;
    }

    if (navigationColl.resources().length() === 0) {
      return;
    }

    const [navigationRes] = navigationColl;

    try {
      dispatch({ type: NAVIGATION_LOADING_STARTED });

      const coll = await navigationRes.link('self').get();

      setNavigationCollection(coll);
      dispatch({ type: NAVIGATION_LOADING_SUCCEEDED });
    } catch (err) {
      dispatch({ type: NAVIGATION_LOADING_FAILED, navigationLoadingError: err });
    }
  }, [state]);

  const setOpen = React.useCallback(
    value => dispatch({ type: SET_OPEN, open: value }),
    [],
  );

  useEffect(() => {
    const cb = async () => {
      const navLink = pageResource?.relationship(fr.relationships.navigation.name)?.link('related');
      if (navLink) {
        await loadNavigationLink(navLink);
      }
    };
    cb();
  }, [pageResource]);


  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      navigationCollection: state.navigationCollection,
      navigationResource: state.navigationResource,
      isNavigationLoading: state.isNavigationLoading,
      navigationLoadingError: state.navigationLoadingError,
      refreshNavigationCollection,
      setNavigationCollection,
      loadNavigationLink,
      setOpen,
      open: state.open,
      navigationHeaderLeft,
      navigationHeaderRight,
      pageResource,
      loadPageLink,
      navPortal,
      setNavPortal,
    };
  }, [
    state,
    refreshNavigationCollection,
    setNavigationCollection,
    loadNavigationLink,
    navigationHeaderLeft,
    navigationHeaderRight,
    pageResource,
    loadPageLink,
    navPortal,
  ]);

  return (
    <NavigationContext.Provider value={memoizedValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Deprecated: you probably want useNavigationContext, only use this if you specifically want to expose yourself to the possibility that there is no navigation context defined
export const useMaybeNavigationContext = () => {
  return React.useContext(NavigationContext);
};

// Use Navigation State Context
const useNavigationContext = () => {
  const context = useMaybeNavigationContext();
  if (context === undefined) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }
  return context;
};

NavigationProvider.defaultProps = {
  pageResource: undefined,
};

NavigationProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  navigationHeaderLeft: PropTypes.object,
  navigationHeaderRight: PropTypes.object,
  pageResource: PropTypes.object,
  loadPageLink: PropTypes.func.isRequired,
};

NavigationProvider.defaultProps = {
  navigationHeaderLeft: undefined,
  navigationHeaderRight: undefined,
};

export {
  NavigationProvider,
  useNavigationContext,
};
